'use client';
import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { Check, ChevronRight, ChevronLeft, Map as MapIcon, User, Smile, Package, MessageSquare } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider';

export function StickyFeatures() {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({ target: container, offset: ["start start", "end end"] });
  
  const textX = useTransform(scrollYProgress, [0.85, 1], ["0%", "55%"]);
  const imgX = useTransform(scrollYProgress, [0.85, 1], ["0%", "-95%"]);

  const { t, locale } = useLanguage();
  const tf = t.landing.features;

  const featuresList = [ 
    { id: 1, tag: tf.optTag, title: tf.optTitle, desc: tf.optDesc, items: tf.optItems, img: locale === 'en' ? "/landing2.0/tps_11zon.webp" : "/landing2.0/tps_11zon.webp", color: "bg-[#667fff]", imgPos: "object-center" },
    { id: 2, tag: tf.srvTag, title: tf.srvTitle, desc: tf.srvDesc, items: tf.srvItems, img: locale === 'en' ? "/landing2.0/bank_en.webp" : "/landing2.0/bank.webp", color: "bg-[#667fff]", imgPos: "object-center" },
    { id: 3, tag: tf.msgTag, title: tf.msgTitle, desc: tf.msgDesc, items: tf.msgItems, img: locale === 'en' ? "/landing2.0/guilds_en.png" : "/landing2.0/guilds.png", color: "bg-[#667fff]", imgPos: "object-center" }
  ];

  return (
    <section ref={container} className="relative h-auto md:h-[300vh] bg-transparent z-20">
      
      <div className="md:hidden block py-16 px-4">
        {featuresList.map((feature) => (
          <div key={feature.id} className="mb-12 last:mb-0">
            <div className="relative h-56 w-full rounded-[20px] overflow-hidden mb-6 border border-[var(--card-border)] bg-[#161616]">
              <Image src={feature.img} alt={feature.title} fill loading="lazy" className={`object-cover ${feature.imgPos || 'object-center'}`} />
            </div>
            <span className={`font-mc inline-block px-3 py-1.5 rounded-md text-[11px] tracking-widest uppercase text-white mb-4 shadow-sm ${feature.color}`}>
              {feature.tag}
            </span>
            <h3 className="font-mc text-2xl uppercase mb-3 text-white">{feature.title}</h3>
            <p className="text-sm sm:text-base text-[#aaaaaa] font-medium leading-relaxed mb-5">{feature.desc}</p>
            <ul className="space-y-2.5">
              {feature.items.map((item: string, i: number) => (
                <li key={i} className="flex items-center gap-3 font-medium text-white text-sm">
                  <Check size={20} strokeWidth={2.5} className="text-[#667fff] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="hidden md:block sticky top-0 h-screen items-center overflow-hidden max-w-[1300px] mx-auto px-8 w-full">
        <div className="relative w-full h-full">
          
          <motion.div style={{ left: textX }} className="absolute w-[40%] h-full flex items-center z-20 pointer-events-none will-change-transform">
             {featuresList.map((feature, index) => (
               <FeatureText key={feature.id} feature={feature} scrollYProgress={scrollYProgress} index={index} total={featuresList.length} />
             ))}
          </motion.div>

          <motion.div style={{ x: imgX }} className="absolute right-0 w-[50%] lg:w-[55%] max-w-[650px] h-[450px] lg:h-[550px] top-1/2 -translate-y-1/2 z-10 will-change-transform">
            <div className="w-full h-full relative isolation-auto">
               {featuresList.map((feature, index) => (
                 <FeatureImage key={feature.id} feature={feature} scrollYProgress={scrollYProgress} index={index} total={featuresList.length} />
               ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

function FeatureText({ feature, scrollYProgress, index, total }: any) {
  const start = index / total; const end = (index + 1) / total; const isLast = index === total - 1;
  const opacity = useTransform(scrollYProgress, index === 0 ? [start, end - 0.1, end] : isLast ? [start, start + 0.1, end] : [start, start + 0.1, end - 0.1, end], index === 0 ? [1, 1, 0] : isLast ? [0, 1, 1] : [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, index === 0 ? [start, end] : isLast ? [start, start + 0.1, end] : [start, start + 0.1, end], index === 0 ? [0, -50] : isLast ? [50, 0, 0] : [50, 0, -50]);
  const pointerEvents = useTransform(scrollYProgress, (v: number) => { if (index === 0 && v < end) return 'auto'; if (isLast && v >= start) return 'auto'; return (v >= start && v < end) ? 'auto' : 'none'; });
  
  return (
    <motion.div style={{ opacity, y, pointerEvents }} className="absolute top-1/2 -translate-y-1/2 left-0 w-full max-w-[460px] xl:max-w-[500px] pr-4 will-change-opacity-transform">
      <span className={`font-mc inline-block px-4 py-1.5 rounded-md text-[11px] tracking-widest uppercase text-white mb-6 shadow-sm ${feature.color}`}>{feature.tag}</span>
      <h3 className="font-mc text-4xl md:text-6xl uppercase mb-6 text-white leading-[1.1]">{feature.title}</h3>
      <p className="text-lg text-[#aaaaaa] font-medium leading-relaxed mb-8">{feature.desc}</p>
      <ul className="space-y-4">
        {feature.items.map((item: string, i: number) => (
          <li key={i} className="flex items-center gap-3 font-medium text-white"><Check size={22} strokeWidth={2.5} className="text-[#667fff] shrink-0" />{item}</li>
        ))}
      </ul>
    </motion.div>
  );
}

function FeatureImage({ feature, scrollYProgress, index, total }: any) {
  const start = index / total; const end = (index + 1) / total; const fadeStart = index === 0 ? 0 : start; const isLast = index === total - 1;
  const opacity = useTransform(scrollYProgress, [fadeStart, fadeStart + 0.05, end - 0.05, end], index === 0 ? [1, 1, 1, 0] : isLast ? [0, 1, 1, 1] : [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [fadeStart, end], [1.05, 1]);
  return (
    <motion.div style={{ opacity }} className="absolute inset-0 w-full h-full will-change-opacity-transform">
      <motion.div style={{ scale }} className="w-full h-full relative rounded-[24px] overflow-hidden bg-[#161616] border border-[var(--card-border)] will-change-transform">
        <Image src={feature.img} alt={feature.title} fill loading="lazy" className={`object-cover ${feature.imgPos || 'object-center'}`} />
      </motion.div>
    </motion.div>
  )
}

export function FeaturesCarousel() {
  const [activeTab, setActiveTab] = useState(0);
  const { t, locale } = useLanguage();
  const tc = t.landing.carousel;

  const carouselList = [
    { id: 0, title: tc.mapTitle, desc: tc.mapDesc, icon: <MapIcon size={18} />, img: locale === 'en' ? "/landing2.0/features/map_en.png" : "/landing2.0/features/map.png", linkText: tc.mapLink, linkUrl: "/map" },
    { id: 1, title: tc.charTitle, desc: tc.charDesc, icon: <User size={18} />, img: locale === 'en' ? "/landing2.0/features/characters_en.png" : "/landing2.0/features/characters.png", linkText: tc.charLink, linkUrl: "/wiki/characters" },
    { id: 2, title: tc.emoteTitle, desc: tc.emoteDesc, icon: <Smile size={18} />, img: locale === 'en' ? "/landing2.0/features/emotes.png" : "/landing2.0/features/emotes.png", linkText: tc.emoteLink, linkUrl: "/wiki/emotes" },
    { id: 3, title: tc.armorTitle, desc: tc.armorDesc, icon: <Package size={18} />, img: locale === 'en' ? "/landing2.0/features/resource.png" : "/landing2.0/features/resource.png", linkText: tc.armorLink, linkUrl: "/wiki/armor-stands" },
    { id: 4, title: tc.chatTitle, desc: tc.chatDesc, icon: <MessageSquare size={18} />, img: locale === 'en' ? "/landing2.0/features/chat-4.png" : "/landing2.0/features/chat-4_ru.png" }
  ];

  const handleNext = () => setActiveTab((prev) => (prev + 1) % carouselList.length);
  const handlePrev = () => setActiveTab((prev) => (prev - 1 + carouselList.length) % carouselList.length);

  return (
      <section className="py-16 md:py-24 px-4 w-full bg-transparent relative z-20">
          <div className="max-w-7xl mx-auto w-full">
              <div className="text-center mb-8 md:mb-12">
                  <h2 className="font-mc text-3xl md:text-5xl uppercase text-white mb-3 md:mb-4">{tc.heading}</h2>
                  <p className="text-[#aaaaaa] text-sm sm:text-base md:text-lg font-medium">{tc.subheading}</p>
              </div>

              <div className="flex items-center justify-center gap-2 md:gap-4 mb-8 md:mb-12">
                  <button onClick={handlePrev} className="w-10 h-10 hidden md:flex items-center justify-center rounded-xl bg-[#161616] hover:bg-[#283059] text-white transition-all border border-white/5 cursor-pointer outline-none"><ChevronLeft size={20} /></button>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar py-2 px-2 max-w-[95vw] md:max-w-[70vw] outline-none">
                      {carouselList.map((item, idx) => (
                          <button 
                              key={item.id} 
                              onClick={() => setActiveTab(idx)} 
                              onMouseEnter={() => {
                                  // Предзагрузка картинки в JS при наведении мыши на кнопку
                                  const img = new window.Image();
                                  img.src = item.img;
                              }}
                              className={`outline-none whitespace-nowrap px-4 md:px-5 py-2 md:py-2.5 rounded-[10px] md:rounded-[12px] text-xs md:text-sm font-medium transition-all duration-300 cursor-pointer ${activeTab === idx ? 'bg-[#667fff] text-white border-transparent' : 'bg-[#161616] text-[#aaaaaa] border border-white/5 hover:bg-[#283059] hover:text-white hover:border-transparent'}`}
                          >
                              {item.title}
                          </button>
                      ))}
                  </div>
                  <button onClick={handleNext} className="w-10 h-10 hidden md:flex items-center justify-center rounded-xl bg-[#161616] hover:bg-[#283059] text-white transition-all border border-white/5 cursor-pointer outline-none"><ChevronRight size={20} /></button>
              </div>

              <div className="relative min-h-min pb-4 md:pb-0 w-full">
                  <AnimatePresence mode="wait">
                      <motion.div key={activeTab} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }} transition={{ duration: 0.3 }} className="bg-[#161616] border border-white/5 rounded-[24px] p-6 md:p-10 lg:p-12 overflow-hidden relative outline-none w-full">
                          <div className="flex flex-col-reverse md:flex-row items-center gap-6 md:gap-12 lg:gap-16 relative z-10 w-full">
                              <div className="flex-1 space-y-4 md:space-y-5 text-center md:text-left flex flex-col items-center md:items-start w-full">
                                  <div className="inline-flex w-12 h-12 rounded-[12px] bg-white/5 border border-white/10 items-center justify-center text-white mb-2 md:mb-4">{carouselList[activeTab].icon}</div>
                                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white">{carouselList[activeTab].title}</h3>
                                  <p className="text-sm md:text-base text-[#aaaaaa] font-medium leading-relaxed">{carouselList[activeTab].desc}</p>
                                  {carouselList[activeTab].linkText && (
                                      <Link href={carouselList[activeTab].linkUrl || "#"} className="inline-flex items-center gap-2 text-[#7a93ff] text-sm md:text-base font-medium hover:gap-3 transition-all cursor-pointer outline-none mt-2">
                                          {carouselList[activeTab].linkText} <ChevronRight size={16} />
                                      </Link>
                                  )}
                              </div>
                              <div className="flex-1 w-full relative h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] rounded-[16px] md:rounded-[20px] overflow-hidden border border-white/5 bg-[#111111] group">
                                  <Image src={carouselList[activeTab].img} alt={carouselList[activeTab].title} fill priority={activeTab === 0} loading={activeTab === 0 ? "eager" : "lazy"} className={`object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.03] ${activeTab === 4 ? 'object-left' : 'object-center'}`} />
                                  <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(0,0,0,0.4)] pointer-events-none rounded-[16px] md:rounded-[20px]" />
                              </div>
                          </div>
                      </motion.div>
                  </AnimatePresence>
              </div>
          </div>

          {/* СКРЫТЫЙ БЛОК ДЛЯ ПРЕДЗАГРУЗКИ ВСЕХ КАРТИНОК В КЭШ БРАУЗЕРА */}
          <div className="hidden" aria-hidden="true">
              {carouselList.map((item) => (
                  <Image key={item.id} src={item.img} alt="" width={1} height={1} priority />
              ))}
          </div>
      </section>
  );
}